import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Grid,
} from "@mui/material";
import { Formik, Form, FormikHelpers } from "formik";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import axios from "../../api";

import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useSelector } from "react-redux";
import SoftButton from "components/SoftButton";
import * as Yup from "yup";
import StatusHistory from "examples/ReuseFunctions/StatusHistory";
import AssigneeHistory from "examples/ReuseFunctions/AssigneeHistory";
import StatusTracker from "examples/ReuseFunctions/StatusTracker";
import { RootState } from "store";
import DateFormat from "examples/ReuseFunctions/DateFormat";

const customScrollbarStyle = {
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#44444461",
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#666",
  },
};
const currentTime = DateFormat();

const validationSchema = Yup.object({
  assigned_employee_id: Yup.number().required("Assignee is required"),
  urgency: Yup.string().required("Urgency is required"),
});

const conditionValidationSchema = Yup.object({
  assigned_employee_id: Yup.number().required("Assignee is required"),
  priority_rank: Yup.string().required("Priority rank is required"),
});

interface Employee {
  employee_id: number;
  name: string;
  phone?: string;
}

interface SelectedEmployee {
  employee_id: number | string;
  name: string;
  phone?: string;
}

interface SelectedStatus {
  status_id?: number | string;
  status_name: string;
}

interface AssignTicketData {
  ticket_id: string | number;
  assigned_employee_id?: string | number;
  employee_name?: string;
  status_id?: string | number;
  status_name?: string;
  priority_rank?: string | null;
  urgency?: string | number;
  assignee_history?: string[];
  status_history?: string[];
  status_tracker?: string[];
}

interface AssignTicketProps {
  isOpen: boolean;
  onClose: () => void;
  fetchData: () => void;
  assignTicketData: AssignTicketData | null;
}

interface FormValues {
  assigned_employee_id: string | number;
  status_id: string | number;
  priority_rank: string | null;
  comment: string;
  urgency: string | number;
}

interface TicketData {
  assigned_employee_id?: number;
  status_id: number;
  priority_rank?: string | null;
  urgency?: string | number;
  assignee_history?: string[] | string;
  status_history?: string[] | string;
  status_tracker?: string[] | string;
  employee_arrival_date: null;
}

const AssignTicket: React.FC<AssignTicketProps> = ({
  isOpen,
  onClose,
  fetchData,
  assignTicketData,
}) => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);

  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    assignTicketData?.assigned_employee_id
      ? {
          employee_id: Number(assignTicketData.assigned_employee_id),
          name: assignTicketData?.employee_name ?? "",
        }
      : null
  );
  const [selectedStatus, setSelectedStatus] = useState<SelectedStatus>({
    status_id: assignTicketData?.status_id,
    status_name: assignTicketData?.status_name ?? "",
  });

  useEffect(() => {
    const fetchEmployeesData = async () => {
      try {
        const employeeslist = await axios.get(`/employee/roles/4`);
        const { data } = employeeslist;
        setEmployees(data?.list ?? []);
      } catch (err) {
        console.error("Error fetching employees data:", err);
      }
    };

    fetchEmployeesData();
  }, []);

  const priorityRanks: string[] = ["High", "Medium", "Low"];
  const urgency: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  // Helper function to convert array or string to comma-separated string
  const formatHistoryData = (
    data: string[] | string | null | undefined
  ): string | null => {
    if (Array.isArray(data)) {
      return data.join(",");
    }
    return data ?? null;
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="otp-modal"
      aria-describedby="otp-modal-description"
    >
      <Box
        className="emp-ticket-assign"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 4,
          minWidth: 500,
          minHeight: 220,
          maxHeight: "calc(100vh - 100px)",
          overflow: "auto",
          ...customScrollbarStyle,
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          Employee Ticket Assign
        </Typography>
        <Formik<FormValues>
          enableReinitialize
          initialValues={{
            assigned_employee_id: assignTicketData?.assigned_employee_id ?? "",
            status_id: assignTicketData?.status_id ?? "",
            priority_rank: assignTicketData?.priority_rank ?? null,
            comment: "",
            urgency: assignTicketData?.urgency ?? "",
          }}
          validationSchema={
            selectedEmployee ? conditionValidationSchema : validationSchema
          }
          onSubmit={async (
            values: FormValues,
            { setSubmitting }: FormikHelpers<FormValues>
          ) => {
            // Helper function to handle assignee history update
            const updateAssigneeHistory = (ticketData: TicketData) => {
              if (
                assignTicketData?.assigned_employee_id !==
                values?.assigned_employee_id
              ) {
                const assignData = AssigneeHistory(
                  formatHistoryData(assignTicketData?.assignee_history),
                  selectedEmployee?.name ?? ""
                );
                ticketData.assignee_history = assignData;
              }
            };

            // Helper function to handle status history update
            const updateStatusHistory = (ticketData: TicketData) => {
              if (assignTicketData?.status_id !== values?.status_id) {
                const statusdata = StatusHistory(
                  formatHistoryData(assignTicketData?.status_history),
                  user?.name ?? "",
                  selectedStatus?.status_name
                );
                ticketData.status_history = statusdata;
              }
            };

            // Helper function to create status tracker
            const createStatusTracker = () => {
              const st = assignTicketData?.status_tracker;
              const previousData =
                typeof st === "string"
                  ? st
                  : Array.isArray(st)
                    ? JSON.stringify(st)
                    : null;
              return StatusTracker({
                previousData,
                message: "Engineer is Assigned",
                statusName: selectedStatus?.status_name ?? "",
                status_id: values?.assigned_employee_id ? 2 : 1,
                createdby: user?.name ?? "",
                employeeName: selectedEmployee?.name ?? "",
                employeePhone: selectedEmployee?.phone ?? "",
                arraivalDate: currentTime,
              });
            };

            const ticketData: TicketData = {
              assigned_employee_id: selectedEmployee?.employee_id as number,
              status_id: selectedEmployee?.employee_id ? 2 : 1,
              priority_rank: values?.priority_rank,
              urgency: values?.urgency,
              employee_arrival_date: null,
            };

            // Update histories
            updateAssigneeHistory(ticketData);
            updateStatusHistory(ticketData);
            ticketData.status_tracker = createStatusTracker();

            try {
              await axios.put(`/tickets/${assignTicketData?.ticket_id}`, {
                ticketData,
              });
              fetchData();
              onClose();
              fetchSuccess("Ticket assigned successfully");
            } catch (err: any) {
              fetchError(err.response?.data?.error ?? "An error occurred");
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, setFieldValue, errors, touched, values }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Assignee
                    </SoftTypography>
                    <Autocomplete<Employee>
                      options={employees ?? []}
                      getOptionLabel={(option) => option.name}
                      value={selectedEmployee}
                      isOptionEqualToValue={(option, value) =>
                        option.employee_id === value.employee_id
                      }
                      onChange={(event, value) => {
                        setFieldValue(
                          "assigned_employee_id",
                          value?.employee_id ?? ""
                        );
                        setSelectedStatus(
                          value?.employee_id
                            ? { status_name: "ToDo" }
                            : { status_name: "Open" }
                        );
                        setSelectedEmployee(
                          value
                            ? {
                                ...value,
                                employee_id: Number(value.employee_id),
                              }
                            : null
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={
                            !!errors.assigned_employee_id &&
                            touched.assigned_employee_id
                          }
                          helperText={
                            touched.assigned_employee_id &&
                            errors.assigned_employee_id
                          }
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                {values?.assigned_employee_id ? (
                  <Grid item xs={6} sm={6}>
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Priority Rank
                      </SoftTypography>
                      <Autocomplete<string>
                        options={priorityRanks ?? []}
                        getOptionLabel={(option) => option}
                        value={values.priority_rank}
                        onChange={(event, value) => {
                          setFieldValue("priority_rank", value ?? null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={
                              !!errors.priority_rank && touched.priority_rank
                            }
                            helperText={
                              touched.priority_rank && errors.priority_rank
                            }
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>
                ) : null}
              </Grid>
              {values?.priority_rank ? (
                <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Urgency Level (1-10)
                    </SoftTypography>
                    <Autocomplete<number>
                      options={urgency ?? []}
                      getOptionLabel={(option) => option.toString()}
                      value={values.urgency as number}
                      onChange={(event, value) => {
                        setFieldValue("urgency", value ?? "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.urgency && touched.urgency}
                          helperText={touched.urgency && errors.urgency}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
              ) : null}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item>
                    <SoftButton
                      className="success-btn"
                      variant="gradient"
                      color="success"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Submit
                    </SoftButton>
                  </Grid>
                  <Grid item>
                    <SoftButton
                      className="cancel-btn"
                      variant="gradient"
                      color="warning"
                      onClick={onClose}
                    >
                      Cancel
                    </SoftButton>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AssignTicket;
